import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { useReactToPrint } from 'react-to-print'

// Components
import { DataTable } from '../DataTable'
import { DownloadIcon } from '../DownloadIcon'
import { Modal } from '../Modal'
import Receipt from './Receipt'
import RefundStatusBadge from './RefundStatusBadge'

// Service
import { getTransactions } from '../../services/purchases.service'

// Utils
import { handlePagination, toast } from '../../utils/helpers'
import {
  formatCurrency,
  getNetAmount,
  getPurchaseDate,
  isRefunded,
  summarizePurchase,
} from './helpers'

const PER_PAGE = 5

/**
 * What a purchase bought, with a badge when money has come back off it.
 *
 * Defined out here rather than inline in the column: a component built during render is
 * a new type every time, which throws away the subtree it renders.
 */
const ItemsCell = ({ transaction }) => (
  <div className="flex flex-row flex-wrap items-center gap-2 py-1">
    <span>{summarizePurchase(transaction)}</span>
    <RefundStatusBadge status={transaction.refundStatus} />
  </div>
)

ItemsCell.propTypes = {
  transaction: PropTypes.object.isRequired,
}

/**
 * What the purchase actually cost, with the original struck through where refunds have
 * come off it, so the row still reconciles against a card statement.
 */
const TotalCell = ({ transaction }) => (
  <div className="flex flex-col py-1">
    <span className="font-medium text-black">{formatCurrency(getNetAmount(transaction))}</span>
    {isRefunded(transaction) && (
      <span className="text-xs text-gray-500 line-through">
        {formatCurrency(transaction.authorizedAmount)}
      </span>
    )}
  </div>
)

TotalCell.propTypes = {
  transaction: PropTypes.object.isRequired,
}

/**
 *
 * PurchaseReceipts
 *
 * - Lists what the exhibitor has bought for an event and lets an admin download the
 *   receipt for any of those purchases
 * - Covers purchases EE staff made on the exhibitor's behalf too; they are the same
 *   transaction records
 * - Shared by the exhibitor's own licenses page and the EE-side exhibitor detail page,
 *   which reach the event exhibitor by different routes — hence the explicit id rather
 *   than reading it off `event`
 */
const PurchaseReceipts = ({ event, eventExhibitorId, organizationName }) => {
  // State
  const [loading, setLoading] = useState(true)
  const [transactions, setTransactions] = useState([])
  const [error, setError] = useState(null)
  const [selectedTransaction, setSelectedTransaction] = useState(null)

  // Pagination
  const [currentPage, setCurrentPage] = useState(1)
  const [totalRows, setTotalRows] = useState(0)
  const [pages, setPages] = useState(null)

  // Ref
  const receiptRef = useRef()

  const BASE_URL = `/events/${event.id}/exhibitors/${eventExhibitorId}/transactions/?limit=`

  // Held as well as toasted: a failed fetch and an exhibitor who has genuinely bought
  // nothing both leave the table empty, and "No purchases yet" is a lie in the first case
  const handleError = (m) => {
    setError(m)
    toast(m, 'error')
  }

  const handlePrint = useReactToPrint({
    content: () => receiptRef.current,
    documentTitle: selectedTransaction ? `Receipt-${selectedTransaction.shortId}` : 'Receipt',
  })

  /**
   * Gets the updated list of purchases; updates pagination.
   * @param {string} url
   */
  const getUpdatedTransactions = async (url) => {
    const response = await getTransactions(url, handleError, setLoading)

    if (response) {
      setError(null)
      setTotalRows(response.count)
      setPages({ next: response.next, previous: response.previous })
      setTransactions(response.results)
    }
  }

  useEffect(() => {
    if (eventExhibitorId) {
      getUpdatedTransactions(`${BASE_URL}${PER_PAGE}`)
    }
  }, [eventExhibitorId])

  /**
   * Stands in for the table when there are no rows to show, saying which of the two
   * reasons it is — nothing bought yet, or the list never arrived.
   */
  const renderPlaceholder = () =>
    error ? (
      <div className="rounded-lg bg-error-light px-4 py-6 text-center text-sm text-error-dark">
        We couldn&apos;t load your purchases. Refresh the page to try again.
      </div>
    ) : (
      <div className="rounded-lg border border-gray-light px-4 py-6 text-center text-sm text-gray-600">
        No purchases yet. Receipts appear here once you buy licenses or device rentals.
      </div>
    )

  return (
    <div className="grid w-full">
      <span className="text-md mb-2 font-semibold">Purchases</span>

      {!loading && !transactions.length ? (
        renderPlaceholder()
      ) : (
        <DataTable
          columns={[
            {
              id: 'date',
              grow: 1,
              name: 'Date',
              selector: (row) => row.submitTime || row.createdAt,
              cell: (row) => (
                <span className="font-medium text-black">
                  {getPurchaseDate(row).format('M/D/YYYY')}
                </span>
              ),
              minWidth: '120px',
            },
            {
              id: 'items',
              grow: 2,
              name: 'Items',
              selector: (row) => row.id,
              cell: (row) => <ItemsCell transaction={row} />,
              minWidth: '220px',
            },
            {
              id: 'order-number',
              grow: 1,
              name: 'Order Number',
              selector: (row) => row.shortId,
              // Short ids run to the model's full 20 characters, which wrapped mid-id
              cell: (row) => <span className="whitespace-nowrap">{row.shortId}</span>,
              minWidth: '200px',
            },
            {
              id: 'total',
              grow: 1,
              name: 'Total',
              // Reads as what the purchase actually cost once refunds come off. Nothing
              // in this table sorts, so this is only what `cell` falls back to
              selector: (row) => getNetAmount(row),
              // The original charge stays visible, struck through, so the row still
              // reconciles against the exhibitor's card statement
              cell: (row) => <TotalCell transaction={row} />,
              minWidth: '100px',
            },
            {
              id: 'receipt',
              grow: 0,
              name: '',
              cell: (row) => (
                <button
                  className="flex flex-row items-center gap-1 whitespace-nowrap px-2 py-0.5 font-bold text-purple hover:rounded-full hover:bg-status-purple"
                  onClick={() => setSelectedTransaction(row)}
                  type="button"
                >
                  <DownloadIcon className="h-4 shrink-0 stroke-purple" />
                  Receipt
                </button>
              ),
              minWidth: '130px',
            },
          ]}
          data={transactions}
          onChangePage={(page) =>
            handlePagination(
              page,
              currentPage,
              PER_PAGE,
              totalRows,
              pages,
              setCurrentPage,
              getUpdatedTransactions,
              BASE_URL,
            )
          }
          pagination
          paginationComponentOptions={{ noRowsPerPage: true }}
          paginationPerPage={PER_PAGE}
          paginationTotalRows={totalRows}
          paginationServer
          progressPending={loading}
        />
      )}

      {selectedTransaction && (
        <Modal
          actions={[
            { type: 'cancel', label: 'Close' },
            { type: 'submit', label: 'Download', onClick: handlePrint },
          ]}
          className="sm:max-w-3xl"
          content={
            <div className="mt-3 max-h-[60vh] overflow-y-auto rounded-lg border border-gray-light">
              <Receipt
                event={event}
                organizationName={organizationName}
                ref={receiptRef}
                transaction={selectedTransaction}
              />
            </div>
          }
          onClose={() => setSelectedTransaction(null)}
          open={!!selectedTransaction}
          setOpen={() => setSelectedTransaction(null)}
          title={`Receipt · ${getPurchaseDate(selectedTransaction).format('M/D/YYYY')}`}
        />
      )}
    </div>
  )
}

PurchaseReceipts.defaultProps = {
  organizationName: null,
}

PurchaseReceipts.propTypes = {
  event: PropTypes.object.isRequired,
  eventExhibitorId: PropTypes.string.isRequired,
  organizationName: PropTypes.string,
}

export default PurchaseReceipts
